import type { PageOwner } from '../shell/owner.ts'
import { spring, tracker, type Motion } from '../ui/spring.ts'
import { layout } from '../ui/tokens.ts'

export interface BackendSwipe { cancel(): void; dispose(): void }

/** The page owns horizontal motion; rows keep vertical scroll and their long press. */
export function backendSwipe(owner: PageOwner, element: HTMLElement, onStep: (step: -1 | 1) => void): BackendSwipe {
  let stop = () => {}, motion: Motion | undefined, offset = 0, swallowed: number | undefined
  const place = (value: number) => { offset = value; element.style.transform = value ? `translateX(${value}px)` : '' }
  const cancel = () => { stop(); motion?.cancel(); motion = undefined; swallowed = undefined; place(0) }
  const settle = (velocity = 0) => {
    motion?.cancel()
    motion = spring(offset, 0, { owner, element, velocity, cause: 'gesture', kind: 'spatial-fast', onFrame: place })
  }
  const down = (event: PointerEvent) => {
    cancel()
    if (!owner.active || owner.disposed || document.hidden || event.button !== 0 || !event.isPrimary || event.pointerType === 'mouse' || matchMedia('(min-width: 1024px)').matches) return
    const bounds = element.getBoundingClientRect(), edge = layout['edge-swipe-zone']
    if (event.clientX < edge || event.clientX > innerWidth - edge) return
    // A nested horizontal scroller gets first refusal, including at either end.
    for (let at = event.target as HTMLElement | null; at && at !== element; at = at.parentElement) {
      if (at.matches('input,textarea,select,[contenteditable]') || at.scrollWidth > at.clientWidth && /auto|scroll/.test(getComputedStyle(at).overflowX)) return
    }
    const id = event.pointerId, x = event.clientX, y = event.clientY, speed = tracker()
    let locked = false, released = false, release = () => {}
    speed.add(x, event.timeStamp)
    const finish = () => {
      if (released) return
      released = true
      window.removeEventListener('pointerdown', second, true)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancelled)
      window.removeEventListener('blur', cancelled)
      document.removeEventListener('visibilitychange', visibility)
      release(); stop = () => {}
    }
    const cancelled = () => { finish(); swallowed = undefined; motion?.cancel(); place(0) }
    const second = (next: PointerEvent) => { if (next.pointerId !== id) cancelled() }
    const visibility = () => { if (document.hidden) cancelled() }
    const move = (next: PointerEvent) => {
      if (next.pointerId !== id) return
      const dx = next.clientX - x, dy = next.clientY - y
      if (!locked) {
        if (Math.abs(dy) >= layout['drag-slop'] && Math.abs(dy) >= Math.abs(dx)) { cancelled(); return }
        if (Math.abs(dx) < layout['drag-slop']) return
        locked = true
      }
      next.preventDefault(); speed.add(next.clientX, next.timeStamp); place(dx)
    }
    const up = (next: PointerEvent) => {
      if (next.pointerId !== id) return
      speed.add(next.clientX, next.timeStamp); finish()
      if (!locked) return
      place(next.clientX - x)
      swallowed = id
      const velocity = speed.velocity(next.timeStamp), side = Math.sign(offset)
      const commit = Math.abs(offset) >= bounds.width * layout['page-commit-fraction'] || Math.abs(velocity) >= layout['page-fling-velocity'] && Math.sign(velocity) === side
      settle(velocity)
      if (commit && side) onStep(side > 0 ? -1 : 1)
    }
    stop = cancelled
    window.addEventListener('pointerdown', second, true)
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', cancelled)
    window.addEventListener('blur', cancelled)
    document.addEventListener('visibilitychange', visibility)
    release = owner.own(finish, 'work')
  }
  const click = (event: MouseEvent) => {
    if (swallowed === undefined || (event instanceof PointerEvent ? event.pointerId !== swallowed : event.detail === 0)) return
    swallowed = undefined; event.preventDefault(); event.stopPropagation()
  }
  element.addEventListener('pointerdown', down)
  element.addEventListener('click', click, true)
  const activity = owner.onActive(active => { if (!active) cancel() })
  const dispose = owner.own(() => {
    cancel(); activity(); element.removeEventListener('pointerdown', down); element.removeEventListener('click', click, true)
  })
  return { cancel, dispose }
}
